import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import queryString from "query-string";

import { COUNTRIES_BY_CODE } from "shared/dictionaries/countries";

import CreditCardForm from "apps/payment/components/CreditCardForm";
import OneClickPurchase from "apps/payment/components/OneClickPurchase";
import Context from "shared/util/Context";
import OkLoader from "shared/components/OkLoader";
import { formatProductName } from "shared/payment/helpers/processProduct";
import AppPhoneCommand from "shared/util/AppPhoneCommand";
import { getPlatform } from "shared/payment/services";
import pickPaymentToken from "shared/payment/helpers/pick-payment-token";
import ThirdPartyPaymentOptions from "apps/payment/components/ThirdPartyPaymentOptions";
import { getSelectedProductByValue } from "shared/payment/helpers/getSelectedProduct";
import Str from "shared/components/Str";
import _str from "shared/util/_str";
import { callbackStatusOnPurchaseEvent } from "shared/payment/analytics";
import {
	SUBTYPE_TO_TOKEN_TYPE_MAP,
	SUBTYPE_TO_FEATURE_MAP,
	PRODUCT_TYPES,
	PRODUCT_TYPE_MAP,
	PRODUCT_SUBTYPES,
	CREDIT_CARD_PAYMENT_PROCESSOR,
} from "shared/payment/constants";

import {
	QUERY,
	CREDIT_CARD_PURCHASE_MUTATION,
	PAYPAL_PURCHASE_MUTATION,
} from "./queries";

import CloseIcon from "shared/components/svgs/close_x.svg";
import "./AndroidPayment.scss";

const AndroidPayment = () => {
	const platform = getPlatform();
	const [hasOneClickPurchase, setOneClickPurchasePage] = useState(false);
	const [isPayPalScreen, setPayPalScreen] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [hasSuccessfullyPurchased, setHasSuccessfullyPurchased] = useState(false);
	const [payPalCancelMessage, setPaypalCancelMessage] = useState(null);

	const params = queryString.parse(window.location.search);
	const formattedProductSubtype = (params.product || "").toUpperCase();
	const productType = PRODUCT_TYPE_MAP[formattedProductSubtype];

	const {
		loading,
		data,
	} = useQuery(QUERY, {
		variables: {
			userId: Context.get("userid"),
			platform,
			productType,
			productSubtype: formattedProductSubtype,
		},
	});

	const [
		submitCreditCardPayment,
		{
			data: creditCardPaymentMutationPayload,
		},
	] = useMutation(CREDIT_CARD_PURCHASE_MUTATION);

	const [
		submitPayPalPayment,
		{
			data: payPalPaymentMutationPayload,
		},
	] = useMutation(PAYPAL_PURCHASE_MUTATION);

	useEffect(() => {
		const storedPaymentMethods = get(data, "user.storedPaymentMethods") || [];
		const hasSavedCreditCard = storedPaymentMethods
			.some((paymentMethod) => (
				paymentMethod.paymentProcessor === CREDIT_CARD_PAYMENT_PROCESSOR
				&& paymentMethod.isValid
			));
		setOneClickPurchasePage(hasSavedCreditCard);
	}, [data]);

	useEffect(() => {
		const creditCardPurchaseResp = get(creditCardPaymentMutationPayload, "purchaseWithCreditCard") || {};
		const payPalPurchaseResp = get(payPalPaymentMutationPayload, "purchaseWithPayPal") || {};
		const isAlist = formattedProductSubtype === PRODUCT_SUBTYPES.ALIST
			|| formattedProductSubtype === PRODUCT_SUBTYPES.ALISTPREMIUM;
		const isPurchaseRespSuccess = payPalPurchaseResp.success || creditCardPurchaseResp.success;

		const isBoost = formattedProductSubtype === PRODUCT_SUBTYPES.BOOST;
		const isReadReceipt = formattedProductSubtype === PRODUCT_SUBTYPES.READ_RECEIPT;
		const isStackPass = formattedProductSubtype === PRODUCT_SUBTYPES.STACK_PASS;

		const products = get(data, "user.rateCard.productPriceInfoList") || [];
		const selectedProduct = getSelectedProductByValue(products, productType, +params.tokensNumber);
		const rateCardName = get(data, "user.rateCard.rateCardName");

		let timer;

		if (selectedProduct && (!isEmpty(creditCardPurchaseResp) || !isEmpty(payPalPurchaseResp))) {
			callbackStatusOnPurchaseEvent({
				isDefaultPaymentType: !isPayPalScreen,
				isSavedCreditCard: hasOneClickPurchase,
				status: isPurchaseRespSuccess,
				productType: selectedProduct.productType,
				productSubType: selectedProduct.productSubType,
				paymentType: !isEmpty(payPalPurchaseResp) ? "paypal" : "cc",
				statusDescription: payPalPurchaseResp.message || creditCardPurchaseResp.message,
				rateCardName,
				unitQuantity: selectedProduct.subPeriod || selectedProduct.tokensCount,
				unitPrice: selectedProduct.formattedInitialUnitPrice,
				totalPrice: selectedProduct.formattedInitialSubtotalPrice,
				subPeriodType: selectedProduct.subPeriodType,
			});
			setIsProcessing(false);
		}

		if (isPurchaseRespSuccess) {
			setHasSuccessfullyPurchased(true);
			timer = setTimeout(() => {
				if (isAlist) {
					AppPhoneCommand("alistPurchaseCompleted");
				} else if (isBoost) {
					AppPhoneCommand("purchasedBoostSuccess", {
						tokenCount: selectedProduct.tokensCount,
					});
					AppPhoneCommand("popActivity");
				} else if (isReadReceipt) {
					AppPhoneCommand("purchasedReadReceiptSuccess", {
						tokenCount: selectedProduct.tokensCount,
					});
					AppPhoneCommand("popActivity");
				} else if (isStackPass) {
					AppPhoneCommand("purchasedStackSuccess");
					AppPhoneCommand("popActivity");
				}
			}, 1000);
		}

		return () => clearTimeout(timer);
	}, [creditCardPaymentMutationPayload, data, formattedProductSubtype,
		hasOneClickPurchase, isPayPalScreen, params.tokensNumber, payPalPaymentMutationPayload,
		productType]);

	useEffect(() => {
		if (params.paypalsuccess) {
			setPayPalScreen(true);
			setIsProcessing(true);

			submitPayPalPayment({
				variables: {
					input: {
						platform,
						productId: params.productid,
						payPalTransactionId: params.token,
						discountId: get(data, "user.rateCard.discountId"),
					},
				},
			});
		}

		if (params.paypalcancel) {
			setPayPalScreen(true);
			setPaypalCancelMessage(_str("PayPal could not process your payment at this time.  Try again or choose another payment method to complete your purchase."));

			const queryParams = queryString.parse(window.location.search);
			delete queryParams.paypalcancel;
			delete queryParams.token;
			const formattedQueryString = queryString.stringify(queryParams);

			if (window.history.pushState) {
				window.history.pushState(null, null, `payments?${formattedQueryString}`);
			}
		}
		// eslint-disable-next-line
	}, []);

	if (loading) {
		return (
			<OkLoader
				className="android-payment-loader"
				show
			/>
		);
	}

	const products = get(data, "user.rateCard.productPriceInfoList") || [];

	const valueParamByProductType = productType === PRODUCT_TYPES.SUBSCRIPTION
		? parseInt(params.subPeriod, 10)
		: parseInt(params.tokensNumber, 10);

	const selectedProduct = getSelectedProductByValue(products, productType, valueParamByProductType);
	const { initialTaxes } = selectedProduct;
	const salesTax = initialTaxes.find((tax) => tax.taxType === "SALES") || {};
	const productTotalPrice = selectedProduct.formattedInitialSubtotalPrice;
	const productPrice = selectedProduct.formattedInitialUnitPrice;
	const productName = formatProductName({
		productType: selectedProduct.productType,
		productSubtype: selectedProduct.productSubType,
		subPeriod: selectedProduct.subPeriod,
		tokensNumber: selectedProduct.tokensCount,
	});

	const onPurchaseCompletePayload = get(creditCardPaymentMutationPayload, "purchaseWithCreditCard") || {};
	const errorResponse = !onPurchaseCompletePayload.success
		? onPurchaseCompletePayload.message
		: null;

	const storedPaymentMethods = get(data, "user.storedPaymentMethods");
	const paymentMethod = pickPaymentToken(storedPaymentMethods);

	const paymentOptions = get(data, "user.rateCard.paymentOptions") || [];
	const hasVATIncluded = initialTaxes.some((tax) => tax.taxType === "VAT");
	const isInternational = selectedProduct.initialCurrency !== COUNTRIES_BY_CODE.US.currency;

	// only show zip code for users in US, UK, and Canada
	const shouldShowZipCode = selectedProduct.initialCurrency === COUNTRIES_BY_CODE.US.currency
		|| selectedProduct.initialCurrency === COUNTRIES_BY_CODE.UK.currency
		|| selectedProduct.initialCurrency === COUNTRIES_BY_CODE.CA.currency;

	const rateCardName = get(data, "user.rateCard.rateCardName");
	const paymentStateAbbreviation = get(data, "user.paymentStateAbbreviation");
	const isUserInWashingtonState = paymentStateAbbreviation === "WA";

	let androidPaymentComponent;

	const sharedPaymentProps = {
		productName,
		productPrice,
		hasVATIncluded,
		isInternational,
		taxAmount: salesTax.formattedAmount,
		productSubtype: selectedProduct.productSubType,
		totalPrice: productTotalPrice,
		convertedProductPrice: selectedProduct.formattedInitialSubtotalPriceBase,
		rateCardName,
		productType: selectedProduct.productType,
		paymentOptions,
		currency: selectedProduct.initialCurrency,
		subscriptionSubPeriod: selectedProduct.subPeriod,
		tokensCount: selectedProduct.tokensCount,
		subPeriodType: selectedProduct.subPeriodType,
		unitPrice: selectedProduct.formattedInitialUnitPrice,
		isProcessing,
		hasSuccessfullyPurchased,
		isUserInWashingtonState,
	};

	if (hasOneClickPurchase || isPayPalScreen) {
		androidPaymentComponent = (
			<OneClickPurchase
				storedCreditCardLastFour={paymentMethod.cardLastFour}
				onSubmit={() => {
					submitCreditCardPayment({
						variables: {
							input: {
								productId: selectedProduct.productId,
								platform,
								tokenId: paymentMethod.tokenId,
							},
						},
					});
					setIsProcessing(true);
				}}
				productId={selectedProduct.productId}
				onPaymentMethodChange={() => {
					setOneClickPurchasePage(false);
					setPayPalScreen(false);
				}}
				isPayPalScreen={isPayPalScreen}
				hasSuccessfullyAuthedPayPal={params.paypalsuccess}
				{...sharedPaymentProps}
			/>
		);
	} else {
		androidPaymentComponent = (
			<CreditCardForm
				shouldShowZipCode={shouldShowZipCode}
				onSubmit={(formValues) => {
					submitCreditCardPayment({
						variables: {
							input: {
								productId: selectedProduct.productId,
								platform,
								cardNumber: formValues.cardNumber.replace(/\D+/g, ""),
								cvv: formValues.cvv,
								expirationDate: formValues.expirationDate.replace(/\D+/g, ""),
								cardHolderName: formValues.fullName,
								zipCode: formValues.zipCode,
								discountId: get(data, "user.rateCard.discountId"),
							},
						},
					});
					setIsProcessing(true);
				}}
				{...sharedPaymentProps}
			/>
		);
	}

	const onPayPalPurchaseCompletePayload = get(payPalPaymentMutationPayload, "purchaseWithPayPal") || {};
	const onPayPalSubmitErrorMessage = !onPayPalPurchaseCompletePayload.success
		&& onPayPalPurchaseCompletePayload.message;

	return (
		<div className="android-payment-container">
			{(errorResponse || onPayPalSubmitErrorMessage || (isPayPalScreen && payPalCancelMessage))
				&& (
					<div className="android-payment-submitted-error">
						{(errorResponse || onPayPalSubmitErrorMessage || payPalCancelMessage)}
					</div>
				)
			}
			<div className="android-payment">
				<div className="android-payment-close-button-container">
					<button
						type="button"
						onClick={() => AppPhoneCommand("popActivity")}
						className="android-payment-close-button"
					>
						<CloseIcon
							height={30}
							width={30}
							className="android-payment-close-icon"
						/>
					</button>
				</div>
				<header className="android-payment-header">
					<Str>Payment Method</Str>
				</header>
				{androidPaymentComponent}
				<ThirdPartyPaymentOptions
					paymentOptions={paymentOptions}
					productId={selectedProduct.productId}
					tokenType={SUBTYPE_TO_TOKEN_TYPE_MAP[selectedProduct.productSubType]}
					feature={SUBTYPE_TO_FEATURE_MAP[selectedProduct.productSubType]}
					isSubscription={selectedProduct.productType === PRODUCT_TYPES.SUBSCRIPTION}
					setPayPalScreen={setPayPalScreen}
					rateCardName={rateCardName}
					productSubtype={selectedProduct.productSubType}
					currency={selectedProduct.initialCurrency}
					subscriptionSubPeriod={selectedProduct.subPeriod}
					productPrice={productPrice}
					subPeriodType={selectedProduct.subPeriodType}
					isPayPalScreen={isPayPalScreen}
					isSavedCreditCard={hasOneClickPurchase}
				/>
			</div>
		</div>
	);
};

export default AndroidPayment;



// WEBPACK FOOTER //
// ./src/apps/payment/containers/AndroidPayment/AndroidPayment.jsx